import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConsentRegion, type ConsentRegion } from "@/lib/consentRegion";

const STORAGE_KEY = "lazona_cookie_consent_v1";
const OPEN_EVENT = "lazona:open-cookie-preferences";

type Consent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  noticeVersion?: string;
  region?: string | null;
  previousAcceptance?: { timestamp: string; noticeVersion?: string };
};

export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function readConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Consent;
    return typeof parsed.analytics === "boolean" && typeof parsed.marketing === "boolean" ? parsed : null;
  } catch {
    return null;
  }
}

function applyConsent(c: Consent) {
  // Google Consent Mode v2 - signal the user's choice to GTM/GA
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  const gtag: (...args: unknown[]) => void =
    w.gtag ||
    function (...args: unknown[]) {
      (w.dataLayer as unknown[]).push(args);
    };
  gtag("consent", "update", {
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
    analytics_storage: c.analytics ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const [region, setRegion] = useState<ConsentRegion | null>(null);

  useEffect(() => {
    let active = true;
    // Default deny before any choice (Consent Mode v2 default)
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    (w.dataLayer as unknown[]).push([
      "consent",
      "default",
      {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
        functionality_storage: "granted",
        security_storage: "granted",
        wait_for_update: 500,
      },
    ]);

    const existing = readConsent();
    if (existing) {
      applyConsent(existing);
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }

    void getConsentRegion().then((resolved) => {
      if (!active) return;
      setRegion(resolved);
      // A saved refusal always wins over geography, including a choice made while lookup was pending.
      const latest = readConsent();
      if (latest) {
        applyConsent(latest);
      } else if (resolved.required) {
        setOpen(true);
      } else {
        // This visit's regional default is not a stored visitor acceptance.
        applyConsent({ necessary: true, analytics: true, marketing: true, timestamp: "" });
      }
    });

    const onOpen = () => {
      const cur = readConsent();
      if (cur) {
        setAnalytics(cur.analytics);
        setMarketing(cur.marketing);
      }
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const current = readConsent();
      if (current) {
        applyConsent(current);
        setAnalytics(current.analytics);
        setMarketing(current.marketing);
        setOpen(false);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      active = false;
      window.removeEventListener(OPEN_EVENT, onOpen);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const save = (a: boolean, m: boolean) => {
    const previous = readConsent();
    const c: Consent = {
      necessary: true,
      analytics: a,
      marketing: m,
      timestamp: new Date().toISOString(),
      noticeVersion: "lazona-cookie-notice-2026-11-02",
      region: region?.country ?? null,
      previousAcceptance: previous?.marketing
        ? { timestamp: previous.timestamp, noticeVersion: previous.noticeVersion }
        : previous?.previousAcceptance,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    } catch {
      /* ignore */
    }
    applyConsent(c);
    setOpen(false);
    setShowDetails(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-4xl rounded-2xl bg-secondary text-secondary-foreground shadow-2xl border border-white/10 p-5 sm:p-6">
        {!showDetails ? (
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 text-sm leading-relaxed">
              <p className="font-display text-base font-semibold mb-1">We value your privacy</p>
              <p className="text-secondary-foreground/80">
                We use cookies to make our site work, analyze traffic, and improve our advertising. You can accept all,
                reject non-essential, or manage preferences. See our{" "}
                <Link to="/cookies" className="underline hover:text-accent">Cookie Policy</Link> and{" "}
                <Link to="/privacy" className="underline hover:text-accent">Privacy Policy</Link>.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 md:shrink-0">
              <button
                type="button"
                onClick={() => save(false, false)}
                className="px-4 py-2 text-sm rounded-lg border border-white/20 hover:bg-white/10 transition"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 text-sm rounded-lg border border-white/20 hover:bg-white/10 transition"
              >
                Manage
              </button>
              <button
                type="button"
                onClick={() => save(true, true)}
                className="px-5 py-2 text-sm rounded-lg bg-accent text-accent-foreground font-semibold hover:opacity-90 transition"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="font-display text-base font-semibold mb-1">Cookie preferences</p>
              <p className="text-sm text-secondary-foreground/80">Choose which categories to allow. Strictly necessary cookies are always active.</p>
            </div>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-white/5">
                <div>
                  <p className="text-sm font-semibold">Strictly necessary</p>
                  <p className="text-xs text-secondary-foreground/70">Required for the site to function.</p>
                </div>
                <span className="text-xs text-secondary-foreground/70">Always on</span>
              </div>
              <label className="flex items-start justify-between gap-4 p-3 rounded-lg bg-white/5 cursor-pointer">
                <div>
                  <p className="text-sm font-semibold">Analytics</p>
                  <p className="text-xs text-secondary-foreground/70">Google Analytics 4, Google Tag Manager.</p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-accent"
                  aria-label="Allow analytics cookies"
                />
              </label>
              <label className="flex items-start justify-between gap-4 p-3 rounded-lg bg-white/5 cursor-pointer">
                <div>
                  <p className="text-sm font-semibold">Advertising / Marketing</p>
                  <p className="text-xs text-secondary-foreground/70">Google Ads conversion &amp; phone-call tracking.</p>
                </div>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-accent"
                  aria-label="Allow advertising cookies"
                />
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button
                type="button"
                onClick={() => save(false, false)}
                className="px-4 py-2 text-sm rounded-lg border border-white/20 hover:bg-white/10 transition"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={() => save(analytics, marketing)}
                className="px-5 py-2 text-sm rounded-lg bg-accent text-accent-foreground font-semibold hover:opacity-90 transition"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
